import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const LABELS = {
  av_track_recovery: { title: "Camera/mic auto-recovery (F1)", hint: "Re-acquires ended or frozen host/guest camera & mic; toast on success. OFF = instant rollback" },
  av_leveler_off: { title: "Skip inert Guest Mic Leveler (F2)", hint: "Stops allocating unused WebAudio processing when guests join. OFF = restore old behaviour" },
  av_ios_mix: { title: "iOS listener mixed audio (F3)", hint: "One mixed output on iPhone/iPad listeners instead of one <audio> per guest. Default OFF" },
  av_ctx_48k: { title: "48 kHz audio context (F4)", hint: "Match AudioContext to 48 kHz capture (iOS crackle). Default OFF" },
  av_uplink_adapt: { title: "Uplink adaptation (F5)", hint: "Lower mic bitrate on sustained packet loss; re-publish mic after reconnect. Default OFF" },
  av_ios_light_encode: { title: "iPhone host light encode (F6)", hint: "540p/24 when ≥2 guests on stage to stop slow-motion video. Default OFF" },
  live_control_cell_v2: { title: "Live stage: control-panel cell for 2–5 boxes (P2)", hint: "ON = 2–5 boxes get a Creator Control Panel cell, portrait too. OFF = original approved layout (2 side-by-side · 3 = 2+1 · 4 = 2×2 · 5 portrait 2/2/1, landscape 3×2 + panel)" },
  age_verification_prompt: { title: "Age verification: show prompt", hint: "ON = members see an optional 'Verify you're 18+' banner + /verify page. Nobody is blocked. Default OFF" },
  age_verification_required: { title: "Age verification: ENFORCE (Panda Guardian)", hint: "ON = age-restricted features require Verified 18+. Owner approval required. Default OFF — do not enable until testing is complete" },
  av_testers_only: { title: "F3–F6 testers only", hint: "ON = F3–F6 apply only to authorized audio testers (admins + tester list); members keep current behaviour. OFF = wider rollout to everyone" },
  audio_telemetry: { title: "Audio diagnostics", hint: "In-room audio probe + Audio Diagnostics panel data" },
  top_badge_challenge_award: { title: "Challenge Top Badge award", hint: "30-day badge prize button on ended challenges" },
};

export default function FeatureFlagsCard({ user }) {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState("");
  const isOwner = !!user?.is_owner;

  useEffect(() => {
    if (!isOwner) return;
    api.get("/owner/feature-flags").then(({ data }) => setFlags(data.flags)).catch(() => setFlags({}));
  }, [isOwner]);

  if (!isOwner || !flags) return null;

  const toggle = async (name, on) => {
    setBusy(name);
    try {
      const { data } = await api.post("/owner/feature-flags", { name, on });
      setFlags(data.flags);
      toast.success(`${LABELS[name]?.title || name} ${on ? "enabled" : "disabled"} in this environment.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update flag."); }
    finally { setBusy(""); }
  };

  return (
    <div className="card-surface p-4" data-testid="feature-flags-card">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Owner · Environment flags</div>
      <p className="text-[11px] text-zinc-500 mb-3">Stored in this environment&apos;s database only — a deploy never copies them to Production.</p>
      <div className="space-y-3">
        {Object.keys(LABELS).map((name) => (
          <div key={name} className="flex items-start justify-between gap-3" data-testid={`feature-flag-${name}`}>
            <div className="min-w-0">
              <div className="text-sm text-white">{LABELS[name].title}</div>
              <div className="text-[11px] text-zinc-500">{LABELS[name].hint}</div>
            </div>
            <Switch checked={!!flags[name]} disabled={busy === name} onCheckedChange={(v) => toggle(name, v)} data-testid={`feature-flag-toggle-${name}`} />
          </div>
        ))}
      </div>
    </div>
  );
}
